/**
 * O assistente (Milestone 3).
 *
 * O fluxo, e as regras que o prendem:
 *
 *   ditar/escrever -> `assistant.interpret()` -> PROPOSTA -> [Confirmar] -> escrita
 *
 * 1. `interpret()` **nunca** escreve. A unica passagem para escrita e o botao
 *    Confirmar desta tela (ADR-0003). Nao ha atalho nenhum, nem com confianca 1.0.
 * 2. Confianca baixa nao produz um palpite: produz uma pergunta.
 * 3. Respostas a perguntas mostram as fontes. Nunca um numero sem origem.
 * 4. Sem internet dizemos isso por palavras e oferecemos o caminho manual —
 *    nunca um botao que nao faz nada.
 * 5. Uma correccao alimenta `assistant.reject(acao, correccao)`, que e o sinal
 *    de aprendizagem.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  BigButton,
  Card,
  List,
  ListItem,
  Screen,
  SectionHeading,
  Spinner,
  Stack,
  TextField,
  VoiceButton,
} from '@flos/ui';
import { AiError, type AiAction } from '@flos/shared';
import { useApp } from '../state/AppContext';
import { ProposalCard } from '../components/ProposalCard';
import { CitationList } from '../components/CitationList';
import type { IntentOutcome } from '../wiring/ports';
import { primaryRoutes } from '../navigation';

type Phase = 'idle' | 'thinking' | 'proposal' | 'clarify' | 'answer' | 'error';

export function AssistantScreen() {
  const { boot, sync, manifests, readOnly } = useApp();
  const navigate = useNavigate();
  const assistant = boot.assistant;

  const [text, setText] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [action, setAction] = useState<AiAction | null>(null);
  const [outcome, setOutcome] = useState<IntentOutcome | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const routes = boot.core.registry.routes();
  const areas = primaryRoutes(routes);
  const offline = !sync.online;

  const reset = (): void => {
    setPhase('idle');
    setAction(null);
    setOutcome(null);
    setMessage(null);
  };

  /** Interpreta. Nada e escrito neste caminho — por desenho. */
  const ask = async (phrase: string): Promise<void> => {
    const trimmed = phrase.trim();
    if (!trimmed) return;
    setText(trimmed);
    setOutcome(null);
    setMessage(null);
    setPhase('thinking');

    try {
      const proposed = await assistant.interpret(trimmed);
      setAction(proposed);

      if (proposed.clarification) {
        // Sem certeza: perguntar, nunca adivinhar.
        setPhase('clarify');
        setMessage(proposed.clarification);
        return;
      }

      // `confirm: false` marca intencoes de LEITURA (perguntas). Essas podem
      // correr de imediato porque nao alteram nada. Tudo o resto espera.
      const descriptor = assistant.intents().find((i) => i.name === proposed.intent);
      if (descriptor?.confirm === false) {
        const answer = await assistant.confirm(proposed);
        setOutcome(answer);
        setPhase('answer');
        return;
      }

      setPhase('proposal');
    } catch (error) {
      setPhase('error');
      setMessage(
        error instanceof AiError
          ? error.userMessage
          : 'Não consegui perceber. Pode repetir de outra forma?',
      );
    }
  };

  /** O unico caminho para escrita. Chamado so por toque em "Confirmar". */
  const confirm = async (): Promise<void> => {
    if (!action) return;
    setBusy(true);
    try {
      const result = await assistant.confirm(action);
      setOutcome(result);
      setPhase('answer');
      await boot.sync.refresh();
    } catch (error) {
      setPhase('error');
      setMessage(
        error instanceof AiError
          ? error.userMessage
          : 'Não consegui concluir. Nada foi alterado.',
      );
    } finally {
      setBusy(false);
    }
  };

  const correct = (): void => {
    // A correccao e o sinal de aprendizagem mais valioso que ha: a frase que ela
    // disse mais o fato de a interpretacao estar errada.
    if (action) assistant.reject(action, text);
    setAction(null);
    setMessage('Não era isso. Diga-me de outra forma, por favor.');
    setPhase('clarify');
  };

  const cancel = (): void => {
    if (action) assistant.reject(action);
    reset();
    setText('');
  };

  const manualPaths = (
    <Stack gap="sm">
      <SectionHeading>Fazer à mão</SectionHeading>
      <List label="Áreas onde pode registrar à mão">
        {areas.map((route) => (
          <ListItem
            key={route.fullPath}
            title={route.title}
            icon={manifests.find((m) => m.id === route.pluginId)?.icon ?? '📁'}
            onClick={() => navigate(route.fullPath)}
          />
        ))}
      </List>
    </Stack>
  );

  return (
    <Screen title="Assistente" back={() => navigate(-1)}>
      <Stack gap="lg">
        {readOnly ? (
          <Banner tone="warning" title="Só leitura">
            {readOnly.message} Posso responder a perguntas, mas não consigo
            salvar nada agora.
          </Banner>
        ) : null}

        {offline ? (
          <Banner tone="warning" title="Sem internet">
            O assistente precisa de ligação para perceber o que diz. Tudo o resto
            do aplicativo continua a funcionar — pode registrar as coisas à mão.
          </Banner>
        ) : null}

        {!offline && !assistant.available ? (
          <Banner tone="info" title="Assistente indisponível">
            {assistant.unavailableReason}
          </Banner>
        ) : null}

        {phase === 'idle' || phase === 'clarify' || phase === 'error' ? (
          <Card>
            <Stack gap="md">
              <p className="app-assistant__prompt">
                {phase === 'clarify' || phase === 'error'
                  ? message
                  : 'Diga o que precisa. Por exemplo: “gastei 43 euros no supermercado”.'}
              </p>

              {!offline && assistant.available ? (
                <div className="app-assistant__voice">
                  <VoiceButton
                    onResult={(spoken) => void ask(spoken)}
                    label="Falar com o assistente"
                  />
                </div>
              ) : null}

              <TextField
                label="Ou escreva aqui"
                value={text}
                onChange={setText}
                multiline
                rows={2}
                placeholder="Escreva o que precisa"
              />
              <BigButton
                icon="➡️"
                fullWidth
                disabled={offline || !assistant.available || text.trim().length === 0}
                onClick={() => void ask(text)}
              >
                Perguntar
              </BigButton>
            </Stack>
          </Card>
        ) : null}

        {phase === 'thinking' ? <Spinner label="A pensar…" /> : null}

        {phase === 'proposal' && action ? (
          <ProposalCard
            action={action}
            busy={busy}
            onConfirm={() => void confirm()}
            onCorrect={correct}
            onCancel={cancel}
          />
        ) : null}

        {phase === 'answer' && outcome ? (
          <Card tone={outcome.ok === false ? 'warning' : 'success'} title="Feito">
            <Stack gap="md">
              <p className="app-assistant__answer">{outcome.message}</p>
              <CitationList
                citations={outcome.citations ?? action?.citations ?? []}
                routes={routes}
                onOpen={(path) => navigate(path)}
              />
              {outcome.route ? (
                <BigButton
                  variant="secondary"
                  icon="👀"
                  fullWidth
                  onClick={() => navigate(outcome.route as string)}
                >
                  Ver
                </BigButton>
              ) : null}
              <BigButton
                variant="ghost"
                icon="🔁"
                fullWidth
                onClick={() => {
                  reset();
                  setText('');
                }}
              >
                Pedir outra coisa
              </BigButton>
            </Stack>
          </Card>
        ) : null}

        {(offline || !assistant.available) && areas.length > 0 ? manualPaths : null}
      </Stack>
    </Screen>
  );
}

export default AssistantScreen;
